import { useState, useCallback } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  IconUsers,
  IconSearch,
  IconLoader2,
  IconPlus,
  IconX,
} from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { api } from "@/lib/api"
import { toast } from "sonner"
import {
  PersonIdentityFields,
  PotentialDuplicates,
  usePotentialDuplicates,
} from "@/components/people/person-identity-form"
import {
  createPersonPayload,
  emptyPersonIdentity,
  displaySsn,
  maskSsn,
  validatePersonIdentity,
  type PersonSearchResult,
  type PotentialPersonMatch,
} from "@/lib/person-identity"

// ── Types ────────────────────────────────────────────────────────────

interface PersonCreated {
  id: string
}

const genderLabels: Record<number, string> = {
  0: "Female",
  1: "Male",
  2: "Trans-Male",
  3: "Trans-Female",
  4: "Gender Non-Conforming",
  5: "Unknown",
}

// ── Component ────────────────────────────────────────────────────────

export default function PeoplePage() {
  const navigate = useNavigate()
  const [searchText, setSearchText] = useState("")
  const [results, setResults] = useState<PotentialPersonMatch[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [searching, setSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newPerson, setNewPerson] = useState(emptyPersonIdentity)

  // Live duplicate detection (mirrors the WinForms create-person flow: a new
  // person cannot be created while potential duplicates exist).
  const { duplicates, checking: checkingDuplicates } = usePotentialDuplicates(newPerson, createOpen)

  const performSearch = useCallback(async () => {
    if (!searchText.trim()) return
    setSearching(true)
    setHasSearched(true)
    try {
      const data = await api.get<PersonSearchResult>(
        `/api/people/search?searchText=${encodeURIComponent(searchText.trim())}`
      )
      setResults(data.matches)
      setHasMore(data.hasMoreMatches)
    } catch (err) {
      console.error("Search failed:", err)
      toast.error("Search failed. Please try again.")
    } finally {
      setSearching(false)
    }
  }, [searchText])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      performSearch()
    }
  }

  const handleCreate = async () => {
    const invalid = validatePersonIdentity(newPerson)
    if (invalid) {
      toast.error(invalid)
      return
    }

    if (duplicates.length > 0) {
      toast.error("Resolve the potential duplicate(s) before creating a new person.")
      return
    }

    setCreating(true)
    try {
      const result = await api.post<PersonCreated>("/api/people", createPersonPayload(newPerson))
      toast.success("Person created successfully.")
      setCreateOpen(false)
      setNewPerson(emptyPersonIdentity)
      navigate(`/people/${result.id}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create person."
      toast.error(message)
    } finally {
      setCreating(false)
    }
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—"
    const date = new Date(dateStr)
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
  }

  return (
    <div className="p-6 space-y-6">
      <div className="app-page-header">
        <div className="flex items-center gap-3">
          <IconUsers className="size-6" />
          <h1 className="text-2xl font-semibold tracking-tight">People</h1>
        </div>
        <Dialog
          open={createOpen}
          onOpenChange={(open) => {
            setCreateOpen(open)
            if (!open) {
              setNewPerson(emptyPersonIdentity)
            }
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <IconPlus className="size-4 mr-2" />
              Create Person
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Person</DialogTitle>
              <DialogDescription>
                Last name, first name, date of birth, and gender are required.
                The Social Security Number is optional but recommended — it is
                used to check for existing records.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <PersonIdentityFields value={newPerson} onChange={setNewPerson} disabled={creating} />
            </div>

            {/* Potential duplicates — block creation until resolved */}
            <PotentialDuplicates
              duplicates={duplicates}
              checking={checkingDuplicates}
              onSelect={(match) => navigate(`/people/${match.id}`)}
            />

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateOpen(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating || duplicates.length > 0}
              >
                {creating && <IconLoader2 className="size-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search bar */}
      <div className="flex gap-2 max-w-xl">
        <div className="relative flex-1">
          <Input
            aria-label="Search people"
            placeholder="Search by name, SSN, or partial SSN..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {searchText && (
            <button
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setSearchText("")
                setResults([])
                setHasSearched(false)
              }}
            >
              <IconX className="size-4" />
            </button>
          )}
        </div>
        <Button aria-label="Search" onClick={performSearch} disabled={searching || !searchText.trim()}>
          {searching ? (
            <IconLoader2 className="size-4 animate-spin" />
          ) : (
            <IconSearch className="size-4" />
          )}
        </Button>
      </div>

      {/* Results */}
      {searching ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : hasSearched ? (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Last Name</TableHead>
                  <TableHead>First Name</TableHead>
                  <TableHead>SSN</TableHead>
                  <TableHead>Date of Birth</TableHead>
                  <TableHead>Gender</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground py-8"
                    >
                      No people found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  results.map((person) => (
                    <TableRow key={person.id}>
                      <TableCell>
                        <Link
                          to={`/people/${person.id}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {person.lastName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/people/${person.id}`}
                          className="text-primary hover:underline"
                        >
                          {person.firstName}
                        </Link>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {displaySsn(person.socialSecurityNumber)}
                      </TableCell>
                      <TableCell>{person.dateOfBirth ? formatDate(person.dateOfBirth) : "—"}</TableCell>
                      <TableCell>
                        {genderLabels[person.gender] ?? "Unknown"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {hasMore && (
            <p className="text-sm text-muted-foreground">
              More results available. Refine your search for better results.
            </p>
          )}
        </>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <IconSearch className="size-12 mx-auto mb-4 opacity-30" />
          <p>Search for a person by name, SSN, or partial SSN to get started.</p>
        </div>
      )}
    </div>
  )
}
